import type { TemporalValue } from './temporal';
import { getTemporalType, isTemporal } from './temporal';

/**
 * `equals` of a single Temporal type. The `constructor` check above the Temporal
 * guard guarantees both operands share a type, so we widen the argument here
 * (mirrors the `compare` cast in `compare-values`).
 */
type TemporalEquals = (other: TemporalValue) => boolean;

/** Minimal object surface we read directly (`fast-deep-equal` touches these). */
interface ComparableObject {
	constructor: unknown;
	valueOf: () => unknown;
	toString: () => unknown;
	[key: string]: unknown;
}

/**
 * Temporal-safe deep equality.
 *
 * Adapted from `fast-deep-equal/es6` (MIT © 2017 Evgeny Poberezkin,
 * https://github.com/epoberezkin/fast-deep-equal). The one behavioural change:
 * Temporal operands are compared by same-type `.equals()` immediately before the
 * `valueOf()` branch, because a Temporal value's `valueOf()` throws by design and
 * would otherwise crash the comparison. Everything else — arrays, `Map`, `Set`,
 * typed arrays, `RegExp`, `Date` and boxed/custom-`valueOf` objects (via
 * `valueOf`), plain objects, and `NaN` — matches `fast-deep-equal/es6`, so this
 * is a safe general-purpose deep-equal, not only a filter/query comparator.
 */
export function isDeepEqual(a: unknown, b: unknown): boolean {
	if (a === b) return true;

	if (a && b && typeof a === 'object' && typeof b === 'object') {
		const objectA = a as ComparableObject;
		const objectB = b as ComparableObject;

		if (objectA.constructor !== objectB.constructor) return false;

		if (Array.isArray(a)) {
			const arrayB = b as unknown[];
			if (a.length !== arrayB.length) return false;

			// Plain loop over `.every()`/`.map()` to avoid a closure per node on this
			// hot path; short-circuits on the first mismatch either way.
			let index = 0;

			for (const value of a) {
				if (!isDeepEqual(value, arrayB[index])) return false;
				index++;
			}

			return true;
		}

		if (a instanceof Map) {
			const mapB = b as Map<unknown, unknown>;
			if (a.size !== mapB.size) return false;

			for (const [key, value] of a) {
				if (!mapB.has(key) || !isDeepEqual(value, mapB.get(key))) return false;
			}

			return true;
		}

		if (a instanceof Set) {
			const setB = b as Set<unknown>;
			if (a.size !== setB.size) return false;

			// Members compared by identity (SameValueZero), as in `fast-deep-equal/es6`.
			for (const value of a) {
				if (!setB.has(value)) return false;
			}

			return true;
		}

		if (ArrayBuffer.isView(a)) {
			const viewB = b as ArrayBufferView;
			// Kind is already matched by the `constructor` check above; comparing raw
			// bytes then covers every view type (incl. DataView) and subarray offset.
			if (a.byteLength !== viewB.byteLength) return false;

			const bytesA = new Uint8Array(a.buffer, a.byteOffset, a.byteLength);
			const bytesB = new Uint8Array(viewB.buffer, viewB.byteOffset, viewB.byteLength);
			let index = 0;

			for (const byte of bytesA) {
				if (byte !== bytesB[index]) return false;
				index++;
			}

			return true;
		}

		// Temporal must be handled before the `valueOf()` branch: a Temporal value's
		// `valueOf()` throws by design. Compare same-type only — `.equals()` across
		// Temporal types throws too — so this branch stays sound on its own rather
		// than relying on the `constructor` check above.
		if (isTemporal(a)) return isTemporal(b) && getTemporalType(a) === getTemporalType(b) && (a.equals as TemporalEquals)(b);

		if (a instanceof RegExp) {
			const regExpB = b as RegExp;
			return a.source === regExpB.source && a.flags === regExpB.flags;
		}

		if (objectA.valueOf !== Object.prototype.valueOf) return objectA.valueOf() === objectB.valueOf();
		if (objectA.toString !== Object.prototype.toString) return objectA.toString() === objectB.toString();

		const keysA = Object.keys(objectA);
		if (keysA.length !== Object.keys(objectB).length) return false;

		for (const key of keysA) {
			if (!Object.prototype.hasOwnProperty.call(objectB, key) || !isDeepEqual(objectA[key], objectB[key])) return false;
		}

		return true;
	}

	// `a === b` already covered equal primitives; the only remaining match is NaN.
	return typeof a === 'number' && Number.isNaN(a) && typeof b === 'number' && Number.isNaN(b);
}
